import { computed, observable, action, reaction } from 'mobx';
import { useRequest } from 'ahooks';
import BaseStore from '@src/utils/store/base-store';
import useMount from '@src/utils/hooks/use-mount';
import qs from '@src/utils/query-string-util';
import { shareToQQZone, shareToWeiBo, shareToTieBa } from '@src/utils/page-share';
import { shareUrl, shareTitle, shareDesc } from '@src/utils/share';
import PageSwiperStore from '@src/components/swiper/page-swiper/store';
import AntdMessage from '@src/components/antd/message';
import copy from '@src/components/copy';
import BGMStore from '@src/business-components/bgm/store';
import SideBarStore from '@src/business-components/sidebar/store';
import DrawSectionStore from '@src/pages/main/components/draw-section/store';
import FeatureSectionStore from '@src/pages/main/components/feature-section/store';
import FocusUsSectionStore from '@src/pages/main/components/focusUs-section/store';
import KvSectionStore from '@src/pages/main/components/kv-section/store';
import ReviewSectionStore from '@src/pages/main/components/review-section/store';
import RoleSectionStore from '@src/pages/main/components/role-section/store';
import WelfareSectionStore from '@src/pages/main/components/welfare-section/store';
import { replaceDownloadLinkPc, replaceDownloadLinkAndroid } from '@src/utils/replace_download_link_by_source';
import { aLink } from '@src/utils/common-util';
import $$tracker from '@src/tracker';

class MainStore extends BaseStore {
  // 全屏swiper
  pageSwiperStore = new PageSwiperStore({});

  // KV 首屏
  kvSectionStore = new KvSectionStore({
    mainStore: this
  });

  //周年特色
  welfareSectionStore = new WelfareSectionStore({
    mainStore: this
  });

  // 周年回顾
  reviewSectionStore = new ReviewSectionStore({
    mainStore: this
  });

  // 角色
  roleSectionStore = new RoleSectionStore({
    mainStore: this
  });

  // 周年特色
  featureSectionStore = new FeatureSectionStore({
    mainStore: this
  });

  // 关注我们
  focusUsSectionStore = new FocusUsSectionStore({
    mainStore: this
  });

  // 抽奖
  drawSectionStore = new DrawSectionStore({
    mainStore: this
  });

  // 侧边栏
  sideBarStore = new SideBarStore({
    changeMenu: index => this.changeMenu(index)
  });

  // 背景音乐
  bgm = new BGMStore({
    localStoreNameSpace: 'dlc3-music',
    audioSrc: 'https://static-www.cbjq.com/video/20251229/dlc35/backmusic.mp3 ',
    musicPlayIcon: require('./images/icon_music_pause.png'),
    musicPauseIcon: require('./images/icon_music_play.png'),
    musicIconRotate: false
  });

  // 用户登录名称
  @observable loginUserAccount = '';

  //测试免登参数
  @observable loginUserParam = '';

  shareData = {
    url: shareUrl(),
    title: shareTitle(),
    des: shareDesc()
  };

  // 渠道来源
  @observable utmSource = '';

  // 分享列表配置
  shareConfig = [
    {
      key: 'qq',
      icon: require('./images/share-qq.png'),
      onShare: () => {
        aLink(
          'http://qm.qq.com/cgi-bin/qm/qr?_wv=1027&k=ESU7Uxq81QGXTsP3h-nIK0-PmckarYBc&authKey=6k8kE4yEbAa2QLwbjqIZiafUue4S28ujhJDro1TAKuZw0WRCbdR5HK0%2B3hyGZyyo&noverify=0&group_code=868421507'
        );
        $$tracker.clickShare('QQ', '顶栏');
      }
    },
    {
      key: 'tieba',
      icon: require('./images/share-tieba.png'),
      onShare: () => {
        shareToTieBa({
          ...this.shareData
        });
        $$tracker.clickShare('贴吧', '顶栏');
      }
    },
    {
      key: 'qqZone',
      icon: require('./images/share-qqZone.png'),
      onShare: () => {
        shareToQQZone({
          ...this.shareData
        });
        $$tracker.clickShare('空间', '顶栏');
      }
    },
    {
      key: 'weibo',
      icon: require('./images/share-weibo.png'),
      onShare: () => {
        shareToWeiBo({
          ...this.shareData,
          url: ''
        });
        $$tracker.clickShare('微博', '顶栏');
      }
    },
    {
      key: 'link',
      icon: require('./images/share-link.png'),
      onShare: () => {
        copy(window.location.href, { successPreText: '复制链接成功，快去分享给好友吧' });
        $$tracker.clickShare('复制链接', '顶栏');
      }
    }
  ];

  // 当前页面索引
  @computed
  get pageSwiperIndex() {
    return this.pageSwiperStore.baseSwiperStore.activeIndex;
  }

  // 切换菜单
  @action
  changeMenu(index) {
    this.pageSwiperStore.baseSwiperStore.onChange(index);
    // 同步侧边栏当前索引
    this.sideBarStore.currentIndex = index;
    this.sideBarStore.sidebarContainerStore.isActive = false;
  }

  // 显示侧边栏
  showSideBar() {
    this.sideBarStore.sidebarContainerStore.isActive = true;
  }

  //登陆状态
  @computed
  get loginState() {
    return !!this.loginUserAccount;
  }

  /**
   * 获取url 免登参数
   * @param {*} name 参数名
   * @returns
   */
  getParameterByName(name) {
    const searchParams = qs.getSearchParams();
    return searchParams[name];
  }

  /**
   * 从URL中删除指定参数
   * @param {string} paramName - 要删除的参数名
   */
  removeUrlParameter(paramName) {
    const url = new URL(window.location);
    url.searchParams.delete(paramName);
    window.history.replaceState(null, '', `${window.location.origin}${url.pathname}${url.search}${url.hash}`);
  }

  /**
   * 点击登录
   */
  onLogin = () => {
    window.XPASS.signin({
      onLoginSuccess: data => {
        window.XPASS.init();
        this.onUpdateConfig();
        $$tracker.clickLogin('success', '');
      }
    });
    const el = document.getElementsByClassName('xfe-common-login-loginTypeGroup')?.[0];
    el.innerHTML =
      '<b>*登录注意*</b><br> 请使用游戏账号（或游戏账号绑定的手机号）登录，以保证游戏奖励能够正确发送至您的游戏邮箱。<br> B站及应用宝渠道用户可以登录游戏，点击活动公告中的跳转链接一键登录本页面。';
  };

  /**
   * 点击退出登陆
   */
  onLogout = () => {
    this.gameLoginOut.run();
  };

  /**
   * 获取本地测试免登参数 仅用于本地测试
   * @param {string} account - 通行证游戏uid
   * @param {string} nick - 角色昵称
   * @param {number} url_encrypt - 是否urlencode（1：是，0：否）
   */
  onGameLogin = (account, nick, url_encrypt = 1) => {
    this.getGameLoginParam.run(account, nick, url_encrypt);
  };

  /**
   * 手动测试游戏免登（用于调试）
   * @param {string} testParams
   */
  testGameLogin = testParams => {
    if (testParams) {
      this.onUseGameLoginParam(testParams);
    }
  };

  /**
   * 使用免登参数登录
   * @param {string} params - 免登参数
   */
  onUseGameLoginParam = params => {
    this.getLoginStatus.run(params);
  };

  /**
   * 检查URL中的免登参数并自动登录
   */
  checkAndAutoGameLogin = () => {
    const params = this.getParameterByName('params');

    if (params) {
      const encodedParams = encodeURIComponent(params);
      this.onUseGameLoginParam(encodedParams);
    }
  };

  // 游戏退出登录
  gameLoginOutAsync() {
    return this.$ztService.logout();
  }

  /**
   * 获取初始化配置
   */
  onGetInit = () => {
    this.getInit.run();
  };

  /**
   * 更新配置项
   */
  onUpdateConfig = () => {
    this.getConfig.run();
  };

  /**
   * 获取初始化配置
   */
  getInitAsync = () => {
    return this.$ztService.getInit();
  };

  /**
   * 获取活动配置项
   */
  getConfigAsync = (loginParams = '') => {
    return this.$ztService.getConfig(loginParams);
  };

  /**
   * 获取游戏免登参数用于本地测试
   * @param {string} account - 通行证游戏uid
   * @param {string} nick - 角色昵称
   * @param {number} url_encrypt - 是否urlencode（1：是，0：否）
   */
  getGameLoginParamAsync = (account, nick, url_encrypt = 1) => {
    return this.$ztService.gameLoginParam(account, nick, url_encrypt);
  };

  /**
   * 游戏免登和获得登录态
   * @param {string} params - 免登参数
   */
  getLoginStatusAsync = params => {
    return this.$ztService.getLoginStatus(params);
  };

  /**
   * 获取登录类型
   */
  getLoginTypeAsync = () => {
    return this.$ztService.getLoginType();
  };

  /**
   * 执行任务
   */
  onExecuteMission = missionId => {
    switch (missionId) {
      case 'focusReview':
        this.drawSectionStore.followH5Callback();
        break;
      case 'watchVideo':
        this.drawSectionStore.watchVideoCallback();
        break;
      default:
        break;
    }
  };

  handleClickStart = () => {
    setTimeout(() => {
      // 检查是否有视频在播放或即将播放
      const hasVideoPlaying = this.kvSectionStore.videoPlayState || this.roleSectionStore.videoPlayState || this.roleSectionStore.roleBgmAudioState;
      if (!hasVideoPlaying) {
        this.bgm.handleChange(true);
      }
    }, 300);
    window.removeEventListener('click', this.handleClickStart);
  };

  use() {
    super.use();

    // 获取初始化配置
    this.getInit = useRequest(async () => await this.getInitAsync(), {
      onSuccess: data => {
        //首页kv视频配置
        this.kvSectionStore.pvVideoUrl = data.pv_url;

        //首页左侧礼包按钮状态
        this.kvSectionStore.giftOpenStatus = data.activity_status;

        // 设置抽奖活动相关状态
        this.drawSectionStore.activityStatus = data.activity_status;

        // 设置登录任务状态
        this.drawSectionStore.loginGameState = data.login_mission_status;

        // 设置H5游戏状态
        this.reviewSectionStore.h5GameStatus = data.H5_games_status;

        // 设置活动特色五图
        if (data.activity_img_url && Array.isArray(data.activity_img_url)) {
          this.featureSectionStore.activityImgUrl = data.activity_img_url.map((url, index) => ({
            label: String(index + 1).padStart(2, '0'),
            image: url
          }));
        }

        // 设置安卡PV
        this.roleSectionStore.roleMsgList[0].rolePv = data.ankaxiya_pv_url;

        // 设置茉莉安PV
        this.roleSectionStore.roleMsgList[1].rolePv = data.molian_pv_url;

        // 设置下载列表配置
        const { down_setting } = data;
        if (down_setting) {
          this.kvSectionStore.down_load_ready = true;
          const downloadConfigMap = [
            { key: 'pc_url', index: 0, processor: replaceDownloadLinkPc },
            { key: 'pc_android_url', index: 1, processor: replaceDownloadLinkAndroid },
            { key: 'pc_app_store_url', index: 2, processor: null },
            { key: 'pc_tap_tap_url', index: 3, processor: null }
          ];
          downloadConfigMap.forEach(config => {
            if (down_setting[config.key]) {
              const url = config.processor ? config.processor(down_setting[config.key]) : down_setting[config.key];
              this.kvSectionStore.downloadList[config.index].href = url;
            }
          });
        }
      },
      onError: e => {
        AntdMessage.error(e?.message);
      }
    });

    // 获取活动配置项
    this.getConfig = useRequest(async loginParams => await this.getConfigAsync(loginParams), {
      onSuccess: data => {
        // 用户登录账号
        this.loginUserAccount = data.account;
        // 用户抽奖次数
        this.drawSectionStore.prizeChance = data.lottery_time;
        // 活动期间登录游戏天数
        this.drawSectionStore.loginGameTimes = data.game_login_time;
        // 当天游戏登录状态
        this.drawSectionStore.todayLoginGameState = data.today_login_mission;
        // 是否完成分享回顾任务
        this.drawSectionStore.review_share_status = data.share_h5;
        // 观看前瞻PV任务状态
        this.drawSectionStore.watchVideoMissionState = data.view_status;
        // 关注微博任务状态
        this.drawSectionStore.focusWeiboMissionState = data.subscribe_weibo_status;
        // 关注B站任务状态
        this.drawSectionStore.focusBiliBiliMissionState = data.subscribe_bili_status;
        // 每日分享任务次数
        this.drawSectionStore.dailyShareMissionTimes = data.daily_share_time;
        // 每日分享任务状态：检查今日是否已完成分享（daily_share_status为1表示今日已完成）
        this.drawSectionStore.dailyShareMissionState = data.daily_share_status === 1;
        // 是否获得实物奖励
        this.drawSectionStore.isGetRareReward = data.is_get_entity_prize;
        // 如果用户在其他页面带过来登录态 上报登录数据
        if (this.loginUserAccount) {
          this.getLoginType.run();
        }
      },
      onError: e => {
        const errorMessage = e.serviceResponse?.message || e.message;
        $$tracker.clickLogin('fail', errorMessage);
        AntdMessage.error(e?.message);
      }
    });

    //执行退出登陆
    this.gameLoginOut = useRequest(async () => await this.gameLoginOutAsync(), {
      manual: true,
      onSuccess: data => {
        window.XPASS.exitPassport();
        window.XPASS.exitThird();
        window.XPASS.exit();
      },
      onError: e => {
        AntdMessage.error(e?.message);
      }
    });

    // 游戏免登和获得登录态
    this.getLoginStatus = useRequest(async params => await this.getLoginStatusAsync(params), {
      manual: true,
      onSuccess: data => {
        AntdMessage.success('登陆成功');
        $$tracker.clickLogin('success', '');
        // 设置用户昵称
        if (data.nickname) {
          this.loginUserAccount = data.nickname;
        }

        // 登录成功后删除URL上的免登参数
        this.removeUrlParameter('params');

        // 免登成功后，更新配置项
        this.onUpdateConfig();

      },
      onError: e => {
        AntdMessage.error(e?.message);
      }
    });

    // 获取登录类型
    this.getLoginType = useRequest(async () => await this.getLoginTypeAsync(), {
      manual: true,
      onSuccess: data => {
        // 根据type返回对应的登录类型字符串
        const loginTypeMap = {
          0: '通行证登录',
          1: '游戏免登'
        };
        const loginTypeStr = loginTypeMap[data.type] || '';
        $$tracker.getCbjqztAccount(data.uid || '', loginTypeStr);
      },
      onError: e => {
        AntdMessage.error(e?.message);
      }
    });

    // 本地测试--获取游戏免登参数 发版前请注释this.onGameLogin();
    this.getGameLoginParam = useRequest(async () => await this.getGameLoginParamAsync('jinshan__79ede91ef8bcccbb120e4a2976395', 'liutenglong2', '0'), {
      manual: true,
      onSuccess: data => {
        if (data.param) {
          this.loginUserParam = data.param;
          //本地测试免登
          this.testGameLogin(this.loginUserParam);
        }
      },
      onError: e => {
        AntdMessage.error(e?.message);
      }
    });

    // 监听页面索引变化
    reaction(
      () => this.pageSwiperIndex,
      currentIndex => {
        this.sideBarStore.currentIndex = currentIndex;
        // 首页不显示侧边栏
        if (currentIndex === 0) {
          this.sideBarStore.sidebarContainerStore.isActive = false;
        }
      }
    );

    useMount(async () => {
      // this.changeMenu(0);

      //开启bgm
      window.addEventListener('click', this.handleClickStart);

      // 首先获取初始化配置
      this.getInit.run();

      //本地测试免登
      // this.onGameLogin();

      // 检查URL中的免登参数并自动登录
      this.checkAndAutoGameLogin();
    });
  }
}

export default MainStore;
